import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface WeighScaleVisualProps {
  leftWeight: number;
  rightWeight: number;
  leftLabel?: string;
  rightLabel?: string;
  unit?: 'g' | 'kg';
  label?: string;
  mode?: 'stimulus' | 'support';
  showQuestion?: boolean;
  type?: 'balance' | 'digital';
}
export function WeighScaleVisual({
  leftWeight,
  rightWeight,
  leftLabel,
  rightLabel,
  unit = 'kg',
  label,
  mode = 'support',
  showQuestion = false,
  type = 'balance'
}: WeighScaleVisualProps) {
  const computed = useMemo(() => {
    const difference = leftWeight - rightWeight;
    const maxTilt = 20; // degrees
    const tiltAngle = Math.max(-maxTilt, Math.min(maxTilt, difference / Math.max(leftWeight, rightWeight, 1) * maxTilt));
    const isBalanced = Math.abs(difference) < 0.01;
    return {
      difference,
      tiltAngle,
      isBalanced,
      heavier: difference > 0 ? 'left' : difference < 0 ? 'right' : 'balanced'
    };
  }, [leftWeight, rightWeight]);
  const scaleViz = type === 'digital' ? <div className="flex justify-center items-center py-4" data-id="element-14786" data-mp="29rm31bskh6q3">
        <svg width="200" height="140" viewBox="0 0 200 140" className="drop-shadow-sm" data-id="element-14787" data-mp="24sixzrcl70i4">
      
          {/* Digital Scale Base */}
          <rect x="20" y="90" width="160" height="30" rx="8" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="2" data-id="element-14788" data-mp="20114focw8276" />
      
          <rect x="30" y="85" width="140" height="10" rx="2" fill="#cbd5e1" data-id="element-14789" data-mp="1v2zeqkkyp4mq" />
          {/* Platform */}
          <path d="M 40 85 L 20 70 L 180 70 L 160 85 Z" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1" data-id="element-14790" data-mp="1qzkutitwk440" />
      
          {/* LCD Screen */}
          <rect x="60" y="95" width="80" height="20" rx="4" fill="#86efac" stroke="#4ade80" strokeWidth="1" data-id="element-14791" data-mp="1h2yd32wvht67" />
      
          <text x="100" y="110" textAnchor="middle" fontFamily="monospace" fontSize="14" fontWeight="bold" fill="#064e3b" data-id="element-14792" data-mp="1c5gbjxl6qg88">
        
            {showQuestion ? '?.??' : `${leftWeight} ${unit}`}
          </text>
          {/* Object on scale */}
          <rect x="70" y="30" width="60" height="40" rx="4" fill="#fde68a" stroke="#b45309" strokeWidth="2" data-id="element-14793" data-mp="36glcgu4w0ejv" />
      
          <text x="100" y="55" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-14794" data-mp="19jyx778mejrl">
        
            {leftLabel || (showQuestion ? '?' : `${leftWeight} ${unit}`)}
          </text>
        </svg>
      </div> : <div className="flex justify-center items-center" data-id="element-14795" data-mp="ztm9551lk8gp">
        <svg width="280" height="160" viewBox="0 0 280 160" className="drop-shadow-sm" data-id="element-14796" data-mp="zs2x5qmz4jkq">
      
          {/* Base */}
          <rect x="110" y="140" width="60" height="15" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1" data-id="element-14797" data-mp="1sit6w1t88u0w" />
      
          <rect x="120" y="130" width="40" height="10" fill="#64748b" data-id="element-14798" data-mp="1ejl8hb4w1pxr" />

          {/* Center post */}
          <rect x="135" y="80" width="10" height="50" fill="#64748b" stroke="#475569" strokeWidth="1" data-id="element-14799" data-mp="1ijvozhqb3yqv" />
      

          {/* Balance beam (animated tilt) */}
          <motion.g initial={{
        rotate: 0
      }} animate={{
        rotate: computed.tiltAngle
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} style={{
        transformOrigin: '140px 80px'
      }} data-id="element-14800" data-mp="369b9j3mw5kk">
        
            {/* Beam */}
            <rect x="40" y="76" width="200" height="8" rx="4" fill="#f59e0b" stroke="#b45309" strokeWidth="1" data-id="element-14801" data-mp="3qrqp5noeabz7" />
        

            {/* Center pivot */}
            <circle cx="140" cy="80" r="6" fill="#fbbf24" stroke="#b45309" strokeWidth="1" data-id="element-14802" data-mp="4kirz8nmpbe9" />
        

            {/* Left chain */}
            <line x1="60" y1="80" x2="60" y2="120" stroke="#94a3b8" strokeWidth="2" data-id="element-14803" data-mp="3d1n7zb4dh8gm" />
        

            {/* Right chain */}
            <line x1="220" y1="80" x2="220" y2="120" stroke="#94a3b8" strokeWidth="2" data-id="element-14804" data-mp="37zo8wdfo0xph" />
        

            {/* Left plate */}
            <ellipse cx="60" cy="120" rx="30" ry="6" fill="#fde68a" stroke="#b45309" strokeWidth="1" data-id="element-14805" data-mp="3i08bbjex889a" />
        
            <ellipse cx="60" cy="117" rx="30" ry="4" fill="#fef3c7" data-id="element-14806" data-mp="2u9kq5wfzvm4s" />

            {/* Left weight label */}
            <text x="60" y="110" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-14807" data-mp="2pa84hf0v0l3s">
          
              {showQuestion && !leftLabel ? '?' : leftLabel || `${leftWeight} ${unit}`}
            </text>

            {/* Right plate */}
            <ellipse cx="220" cy="120" rx="30" ry="6" fill="#fde68a" stroke="#b45309" strokeWidth="1" data-id="element-14808" data-mp="1wj51lfc0pqwf" />
        
            <ellipse cx="220" cy="117" rx="30" ry="4" fill="#fef3c7" data-id="element-14809" data-mp="26bassmug44ak" />

            {/* Right weight label */}
            <text x="220" y="110" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-14810" data-mp="2bann1snncdn6">
          
              {showQuestion && !rightLabel ? '?' : rightLabel || `${rightWeight} ${unit}`}
            </text>
          </motion.g>

          {/* Balance indicator */}
          {computed.isBalanced && mode === 'support' && <motion.g initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.6,
        duration: 0.4
      }} data-id="element-14811" data-mp="2p93wvsgt2k9j">
        
              <circle cx="140" cy="50" r="16" fill="#10b981" opacity="0.2" data-id="element-14812" data-mp="uwxby1y40p41" />
              <text x="140" y="55" textAnchor="middle" fontSize="14" fill="#059669" fontWeight="bold" data-id="element-14813" data-mp="ncwcfderzkw3">
          
                ⚖️
              </text>
            </motion.g>}
        </svg>
      </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-sm mx-auto" data-id="element-14814" data-mp="dn33yl9xjlw2">
        {label && <p className="text-sm font-semibold text-slate-700 mb-2 text-center" data-id="element-14815" data-mp="8lnhcos3t7bk">
            {label}
          </p>}
        {scaleViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-amber-100 overflow-hidden w-full max-w-sm mx-auto shadow-sm" data-id="element-14816" data-mp="11c4fwihz1f63">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-100" data-id="element-14817" data-mp="wbzy8p5bueea">
        <h3 className="text-sm font-bold text-amber-900 text-center" data-id="element-14818" data-mp="sc91m4t4d41u">
          {label || 'Weegschaal'}
        </h3>
      </div>

      <div className="p-4 bg-white" data-id="element-14819" data-mp="3h4bh4rtppxkg">{scaleViz}</div>

      <div className="bg-amber-50/50 p-4 border-t border-amber-50" data-id="element-14820" data-mp="3d1yguixqirv2">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-14821" data-mp="3d0ndgk87bm4h">
          {type === 'digital' ? <>
              De weegschaal geeft{' '}
              <strong data-id="element-14822" data-mp="3jigoz0eyeror">
                {leftWeight} {unit}
              </strong>{' '}
              aan.
            </> : computed.isBalanced ? <>
              De weegschaal is <strong data-id="element-14823" data-mp="39k0x2v83ree7">in evenwicht</strong>. Beide kanten wegen
              even zwaar.
            </> : <>
              De{' '}
              <strong data-id="element-14824" data-mp="2vsckyrxuspx8">
                {computed.heavier === 'left' ? 'linker' : 'rechter'}
              </strong>{' '}
              kant is zwaarder en zakt naar beneden.
            </>}
        </p>
      </div>
    </div>;
}